import dayjs from "dayjs";
import { useMemo } from "react";
import {
    getInvoiceDebtListThunk,
    getMarketInfoCollectionThunk,
    getMarketInventoryGetThunk,
    getOrderClosedCheckoutListThunk,
    getOrderOpenedCheckoutListThunk,
    getTaskListsThunk,
    patchMarketChangePhoto,
    patchMarketInventorySet,
    patchMarketSetShelfSize,
    postGeoAdd,
    postInvoiceMakePayment,
    postMarketChangeGeo,
    postOrderCreate,
    postOrderSave,
    postOrderSaveOne,
    postTaskAddDebtTask,
    postTaskAddDistributionPrices,
    postTaskAddRejectTask,
    postTaskAddSaleTask,
    postTaskComplete,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { extractBase64Content } from "tm-shared/utils";

import { MANAGER_AGENT_VOCABULARY } from "@/constants/domain/managerAgentVocabulary";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { photoStorage } from "@/Services/PhotoStorage";
import { uploadPhoto } from "@/Services/UploadPhoto";
import {
    QueueCommand,
    QueueCommandMap,
    QueueCommandMethods,
    QueueCommandType,
} from "@/types/requestQueue";

import { isManagerRole } from "../isManagerRole";

export const makeCommand = <T extends QueueCommandType>(
    cmd: Omit<QueueCommand<T>, "method">,
): QueueCommand<T> => ({
    ...cmd,
    method: QueueCommandMethods[cmd.type] ?? "custom function",
});

type CommandHandler<T extends QueueCommandType> = (
    params: QueueCommandMap[T],
) => Promise<unknown>;

type CommandHandlers = { [K in QueueCommandType]: CommandHandler<K> };

export const useRequestQueueCommands = (): CommandHandlers => {
    const { id: userId, role } = useAppSelector((s) => s.user);
    const { markets } = useAppSelector((s) => s.work);
    const { activeSchedule } = useAppSelector((s) => s.sv);
    const dispatch = useAppDispatch();

    return useMemo(
        () =>
            ({
                postGeoAdd: async (p) => postGeoAdd(p),

                patchMarketInventorySet: async (p) =>
                    patchMarketInventorySet(p),
                postTaskComplete: async (p) => postTaskComplete(p),
                getMarketInventoryGetThunk: async (p) =>
                    dispatch(getMarketInventoryGetThunk(p)),

                postTaskAddSaleTask: async (p) => postTaskAddSaleTask(p),
                postOrderCreate: async (p) => postOrderCreate(p),
                postOrderSave: async (p) => postOrderSave(p),
                postMarketChangeGeo: async (p) => postMarketChangeGeo(p),
                postFileUpload: async (p) => uploadPhoto(p),
                postAddDistributionPrices: async (p) =>
                    postTaskAddDistributionPrices(p),

                postTaskAddRejectTask: async (p) => postTaskAddRejectTask(p),
                postOrderSaveOne: async (p) => postOrderSaveOne(p),
                postTaskAddDebtTask: async (p) => postTaskAddDebtTask(p),
                patchMarketSetShelfSize: async (p) =>
                    patchMarketSetShelfSize(p),
                postInvoiceMakePayment: async (p) => postInvoiceMakePayment(p),
                getInvoiceDebtListThunk: async (p) =>
                    dispatch(getInvoiceDebtListThunk(p)).unwrap(),
                getMarketInfoCollectionWorkThunk: async (p) =>
                    dispatch(getMarketInfoCollectionThunk(p)).unwrap(),

                deletePhotoFromStorage: async (p) =>
                    photoStorage.deletePhoto(p),

                updatePhotoStorage: async (photoIds) => {
                    const photosIdsForDelete = new Set<string>();
                    const photosIdsWithError = new Set<string>();

                    for (const photoId of photoIds) {
                        if (!photoId) {
                            continue;
                        }

                        const info = await photoStorage.getPhotoInfo(photoId);
                        if (!info) continue;

                        if (info.uploaded) {
                            photosIdsForDelete.add(photoId);
                        } else {
                            photosIdsWithError.add(photoId);
                        }
                    }

                    if (photosIdsWithError.size > 0) {
                        await Promise.allSettled(
                            [...photosIdsWithError].map((id) =>
                                photoStorage.updatePhotoInfo(id, {
                                    taskComplete: true,
                                }),
                            ),
                        );
                    }

                    if (photosIdsForDelete.size > 0) {
                        await photoStorage.deletePhotosByIds([
                            ...photosIdsForDelete,
                        ]);
                    }
                },

                putMarketChangePhoto: async ({ photoId, marketId }) => {
                    const base64 =
                        (await photoStorage.getPhoto(photoId)) ?? null;
                    const photoInfo = await photoStorage.getPhotoInfo(photoId);

                    if (!base64 || !photoInfo) {
                        throw new Error(
                            `Photo ${photoId} not found in storage`,
                        );
                    }

                    const normalizedBase64 = extractBase64Content(base64);

                    await patchMarketChangePhoto({
                        marketId,
                        filename: photoInfo.name,
                        base64ContentPhoto: normalizedBase64,
                    });
                },

                updateCheckout: async () => {
                    const isManager = isManagerRole(role);

                    const trueAgentId = isManager ? activeSchedule : userId;

                    await dispatch(
                        getOrderOpenedCheckoutListThunk({
                            agentId: trueAgentId,
                        }),
                    ).unwrap();

                    await dispatch(
                        getOrderClosedCheckoutListThunk({
                            agentId: trueAgentId,
                            targetDate: dayjs().format(backendDateFormat.date),
                        }),
                    ).unwrap();
                },

                getTaskListsThunk: async () => {
                    const roleSlug = isManagerRole(role)
                        ? MANAGER_AGENT_VOCABULARY[role]
                        : undefined;

                    dispatch(
                        getTaskListsThunk({
                            targetIds: Object.keys(markets),
                            roleSlug,
                        }),
                    );
                },
            }) satisfies CommandHandlers,
        [dispatch, userId, markets, activeSchedule, role],
    );
};
